import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Star, XCircle, CalendarDays, Check } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ReviewSubmit() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [author, setAuthor] = useState("");
  const [location, setLocation] = useState("");
  const [text, setText] = useState("");

  useEffect(() => {
    api.get(`/review/${token}`)
      .then((r) => {
        setInfo(r.data);
        setAuthor(r.data.guest_name || "");
        if (r.data.already_reviewed) setDone(true);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!text.trim()) { setError("Please write a short review."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/review/${token}`, { author: author.trim(), location: location.trim(), rating, text: text.trim() });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10" data-testid="review-submit-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>

        {loading && <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>}

        {!loading && error && !info && (
          <div className="text-center" data-testid="review-error">
            <XCircle className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && info && done && (
          <div className="text-center py-4" data-testid="review-thankyou">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-primary" /></div>
            <h1 className="font-serif text-2xl font-semibold text-foreground mb-2">Thank you!</h1>
            <p className="text-sm text-muted-foreground">Your review has been submitted and will appear on our site once it's approved. We appreciate you taking the time — we'd love to host you again!</p>
            <Button asChild className="rounded-full bg-primary mt-6"><Link to="/">Back to site</Link></Button>
          </div>
        )}

        {!loading && info && !done && (
          <>
            <div className="text-center mb-6">
              <p className="text-primary font-medium text-xs uppercase tracking-widest mb-1">Share your experience</p>
              <h1 className="font-serif text-2xl font-semibold text-foreground">How was your stay{info.guest_name ? `, ${info.guest_name.split(" ")[0]}` : ""}?</h1>
            </div>

            {(info.check_in || info.property_name) && (
              <div className="rounded-xl bg-accent/50 p-4 text-sm mb-6 flex items-center gap-2 text-foreground">
                <CalendarDays className="h-4 w-4 text-primary flex-shrink-0" />
                <span>{info.property_name}{info.check_in ? ` · ${info.check_in} → ${info.check_out}` : ""}</span>
              </div>
            )}

            <div className="mb-5">
              <Label className="mb-2 block">Your rating</Label>
              <div className="flex gap-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="transition-transform hover:scale-110"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    <Star className={`h-8 w-8 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Your name</Label><Input value={author} onChange={(e) => setAuthor(e.target.value)} className="rounded-lg" data-testid="review-author-input" /></div>
                <div><Label>City (optional)</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Denver, CO" className="rounded-lg" data-testid="review-location-input" /></div>
              </div>
              <div>
                <Label>Your review</Label>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Tell future guests what you loved about your stay…" className="rounded-lg" data-testid="review-text-input" />
              </div>
            </div>

            {error && <p className="text-sm text-destructive text-center mt-3" data-testid="review-submit-error">{error}</p>}

            <Button className="w-full rounded-full bg-primary h-12 text-base mt-5" disabled={busy} data-testid="review-submit-btn" onClick={submit}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit review"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
